/**
 * Folgeunterlage „Handbuch der Koordinierung" — Entwurf aus der Planungsphase.
 *
 * Legt das Handbuch ueber `ensureHandbuch` als ENTWURF an (derselbe Weg wie
 * der Knopf „Handbuch anlegen": Status `entwurf`, Katalogfassung, LEERE
 * Baustellenordnung und Einrichtung) und zieht die Gewerkeseiten aus der
 * neuesten Planfassung nach — derselbe Weg wie „Gewerke aus dem Plan
 * nachziehen" (`gewerkeAusPlan` → `entwurfAusGewerk` → `createHandbuchGewerke`,
 * je Seite die geforderten Nachweise mit Status `fehlt`).
 *
 * Anhang II: die Planungsphase stellt fest, welche Nummern im PROJEKT
 * zutreffen — welches Gewerk sie ausfuehrt, weiss nur der Koordinator. Das
 * Handbuch hat dafuer kein Vermerkfeld (die Gewerkeseite zeigt den Hinweis
 * `anhangIIPlanungHinweise` live), deshalb steht die Pruefung als Satz in
 * `offen`. Kreuze werden NIE gesetzt — was eine neue Seite angekreuzt traegt,
 * kommt unveraendert aus der Gewerkvorlage (`entwurfAusGewerk`).
 *
 * ═══ HAUSREGEL ════════════════════════════════════════════════════════════
 * Gibt nie frei, versendet nichts, legt nichts in die Ablage, bucht nichts.
 * Gibt es fuer das Projekt schon ein Handbuch (Unique-Index `project_id`),
 * wird NICHTS geschrieben — auch nicht „nur die fehlenden Gewerke". Traegt
 * ein scheinbar frisches Handbuch schon Gewerkeseiten (zweites Fenster),
 * werden weder Seiten noch Nachweise angelegt.
 *
 * Aufbau: oben die puren Teile, unten die duennen Aufrufe der vorhandenen
 * Dienste (`handbuchService`). `legeAn` lehnt nie ab.
 */

import { describeError } from '../errorMessage';
import {
    createHandbuchGewerke,
    createHandbuchNachweise,
    ensureHandbuch,
    fetchHandbuch,
    fetchHandbuchGewerke,
    ladePlanStand,
    type SchreibKontext,
} from '../handbuchService';
import { baueSteckbrief, OHNE_LESEFEHLER, type ProjektSteckbrief } from '../projektSteckbrief/steckbrief';
import {
    anhangIIPlanungHinweise,
    entwurfAusGewerk,
    nachweisEntwuerfe,
} from '../../components/handbuch/handbuchLogic';
import type {
    GewerkAusPlan,
    Handbuch,
    HandbuchGewerk,
    NeuesHandbuchGewerk,
} from '../../components/handbuch/types';
import { istAnhangIINummer, type AnhangIINummer } from '../../data/anhang2Bruecke';
import type { BestandStand, ErzeugungsErgebnis, FolgeErzeuger, PlanungsQuelle } from './typen';

const ART = 'handbuch' as const;
const TITEL = 'Handbuch der Koordinierung';

// ═══════════════════════════════════════════════════════════════════════════
// PURE TEILE
// ═══════════════════════════════════════════════════════════════════════════

const pad2 = (n: number): string => String(n).padStart(2, '0');

/** Tag in deutscher Schreibweise — ISO-Tag zerlegt, Zeitstempel in Ortszeit. */
export const tagDeutsch = (wert: string | null | undefined): string => {
    const s = String(wert || '').trim();
    if (!s) return '';
    const tag = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
    if (tag) return `${tag[3]}.${tag[2]}.${tag[1]}`;
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return '';
    return `${pad2(d.getDate())}.${pad2(d.getMonth() + 1)}.${d.getFullYear()}`;
};

const aufzaehlung = (werte: readonly string[], max = 6): string => {
    const liste = werte.filter(Boolean);
    if (liste.length > max) return `${liste.slice(0, max).join(', ')} …`;
    if (liste.length <= 1) return liste.join('');
    return `${liste.slice(0, -1).join(', ')} und ${liste[liste.length - 1]}`;
};

/** Steckbrief aus der gespeicherten Planung (Lesefehler gibt es hier keine). */
export const steckbriefAusQuelle = (q: PlanungsQuelle): ProjektSteckbrief =>
    baueSteckbrief({
        metadata: q.projektDaten,
        phase: q.phase,
        fragen: q.fragen || [],
        sigeSchritte: [],
        lesefehler: { ...OHNE_LESEFEHLER },
    }, q.heuteIso);

/** Bestand → Satz, z. B. „Entwurf vom 12.10.2026" / „freigegeben am …". */
export const handbuchBestandText = (
    hb: Pick<Handbuch, 'status' | 'created_at' | 'updated_at' | 'freigegeben_am' | 'version_label'>,
): string => {
    const label = (hb.version_label || '').trim();
    const fassung = label ? ` (Fassung ${label})` : '';
    if (hb.status === 'freigegeben') {
        const tag = tagDeutsch(hb.freigegeben_am);
        return `${tag ? `freigegeben am ${tag}` : 'freigegeben'}${fassung}`;
    }
    if (hb.freigegeben_am) {
        const tag = tagDeutsch(hb.freigegeben_am);
        return `Entwurf einer neuen Fassung${fassung}${tag ? `, zuletzt freigegeben am ${tag}` : ''}`;
    }
    const tag = tagDeutsch(hb.created_at);
    return `${tag ? `Entwurf vom ${tag}` : 'Entwurf'}${fassung}`;
};

/**
 * Ist das Handbuch, das `ensureHandbuch` zurueckgab, wirklich eben erst
 * entstanden? `ensureHandbuch` liefert bei einem Wettlauf den VORHANDENEN
 * Satz — dann gehoert er jemand anderem, und hier wird nichts geschrieben.
 * Ein frischer Satz ist Entwurf, ohne Historie und nie freigegeben.
 */
export const istFrisch = (hb: Pick<Handbuch, 'status' | 'historie' | 'freigegeben_am'>): boolean =>
    hb.status === 'entwurf' && !(hb.historie || []).length && !hb.freigegeben_am;

/** Gewerkeseiten-Entwuerfe in Planreihenfolge — dieselben wie beim Nachziehen. */
export const gewerkEntwuerfe = (gewerke: readonly GewerkAusPlan[]): NeuesHandbuchGewerk[] =>
    gewerke.map((g, i) => entwurfAusGewerk(g, i));

/**
 * Bejahte Anhang-II-Nummern, die auf KEINER der Seiten angekreuzt sind
 * (`anhangIIPlanungHinweise` je Seite: eine Nummer gilt als abgedeckt, sobald
 * einer ihrer Wortlaute angekreuzt ist). Ohne Seiten: alle.
 */
export const anhangIIOhneKreuz = (
    seiten: readonly Pick<HandbuchGewerk, 'anhang_ii'>[],
    nummern: readonly number[] | null | undefined,
): AnhangIINummer[] => {
    const gueltig = [...new Set((nummern || []).filter(istAnhangIINummer))].sort((a, b) => a - b);
    if (!seiten.length) return gueltig;
    const offenJeSeite = seiten.map(s => new Set(anhangIIPlanungHinweise(s, gueltig).map(h => h.nummer)));
    return gueltig.filter(n => offenJeSeite.every(offen => offen.has(n)));
};

/** Der Satz zur Anhang-II-Pruefung — leer, wenn die Planung keine Nummer bejaht. */
export const anhangIISaetze = (
    nummern: readonly number[] | null | undefined,
    ungeklaert: readonly number[],
    seiten: readonly Pick<HandbuchGewerk, 'anhang_ii'>[],
): string[] => {
    const saetze: string[] = [];
    const gueltig = [...new Set((nummern || []).filter(istAnhangIINummer))].sort((a, b) => a - b);
    if (gueltig.length) {
        const ohne = anhangIIOhneKreuz(seiten, gueltig);
        const rest = !seiten.length
            ? ' — noch keine Gewerkeseite; das Kreuz setzt der Koordinator je Gewerk.'
            : ohne.length
                ? ` — Nr. ${ohne.join(', ')} auf keiner Gewerkeseite angekreuzt; das Kreuz setzt der Koordinator je Gewerk.`
                : ' — die Kreuze der Gewerkvorlagen je Seite bestätigen.';
        saetze.push(`Anhang II prüfen: Nr. ${gueltig.join(', ')} laut Planungsphase zutreffend${rest}`);
    }
    if (ungeklaert.length) {
        saetze.push(`Anhang II Nr. ${ungeklaert.join(', ')} ${ungeklaert.length > 1 ? 'sind' : 'ist'} in der Planungsphase noch ungeklärt.`);
    }
    return saetze;
};

/** Saetze zu den angelegten Gewerkeseiten. */
export const gewerkSaetze = (
    angelegt: readonly Pick<HandbuchGewerk, 'gewerk_label'>[],
    ohneKatalog: readonly string[],
): { uebernommen: string[]; offen: string[] } => {
    const uebernommen: string[] = [];
    const offen: string[] = [];
    if (angelegt.length) {
        uebernommen.push(`${angelegt.length} Gewerkeseite(n) aus dem SiGe-Plan angelegt: ${aufzaehlung(angelegt.map(g => g.gewerk_label))}.`);
    }
    if (ohneKatalog.length) {
        offen.push(`Keinem Katalog-Gewerk zugeordnet: ${aufzaehlung(ohneKatalog)} — Vorlage auf der Gewerkeseite von Hand wählen.`);
    }
    return { uebernommen, offen };
};

/**
 * Das Handbuch traegt schon Gewerkeseiten, obwohl es eben erst entstanden
 * aussieht (`istFrisch`): ein zweites Fenster hat es zeitgleich angelegt und
 * schon Seiten nachgezogen. Dann legt der Erzeuger keine Seite und keinen
 * Nachweis an — sonst kaemen Nachweise doppelt.
 */
export const gewerkeSchonDaSatz = (anzahl: number): string =>
    `Das Handbuch hat bereits ${anzahl} Gewerkeseite(n) — vermutlich zeitgleich in einem zweiten Fenster angelegt. `
    + 'Keine Seiten aus dem SiGe-Plan angelegt; fehlende im Handbuch über „Gewerke aus dem Plan" nachziehen.';

/** Was die Planung fuer das Handbuch nicht hergibt — die Pflichtkapitel. */
export const REST_IM_HANDBUCH = 'Vergabemodell, Baustellenordnung, Baustelleneinrichtung und je Gewerk Firma und Verantwortlichen im Handbuch festlegen.';

// ═══════════════════════════════════════════════════════════════════════════
// DIENSTE — duenn
// ═══════════════════════════════════════════════════════════════════════════

const ohneProjekt = (projectId: string): boolean => !projectId || projectId === 'temp-id';

/** Liest nur (`fetchHandbuch` legt nichts an, Hausregel (d) im Dienst). */
const leseBestand = async (projectId: string): Promise<BestandStand> => {
    if (ohneProjekt(projectId)) {
        return { status: 'fehler', fehler: 'Ohne gespeichertes Projekt gibt es kein Handbuch.' };
    }
    try {
        const hb = await fetchHandbuch(projectId);
        return hb ? { status: 'vorhanden', beschreibung: handbuchBestandText(hb) } : { status: 'keiner' };
    } catch (e: unknown) {
        return { status: 'fehler', fehler: `Das Handbuch konnte nicht gelesen werden: ${describeError(e)}` };
    }
};

/**
 * Schreibkontext des Erzeugers. Der Vertrag (`PlanungsQuelle`) traegt keinen
 * Nur-Ansicht-Schalter — ein Projekt in Nur-Ansicht ruft den Erzeuger gar
 * nicht erst auf (Sache des Aufrufers). Der Status ist der des eben
 * angelegten Satzes, damit `pruefeAenderbar` im Dienst weiter greift.
 */
const kontext = (hb: Pick<Handbuch, 'status'>): SchreibKontext => ({ nurAnsicht: false, status: hb.status });

const fehlerErgebnis = (fehler: string): ErzeugungsErgebnis => ({ status: 'fehler', art: ART, fehler });

const legeAnOhneSperre = async (q: PlanungsQuelle): Promise<ErzeugungsErgebnis> => {
    if (ohneProjekt(q?.projectId)) return fehlerErgebnis('Ohne gespeichertes Projekt kann kein Handbuch angelegt werden.');

    const vorher = await leseBestand(q.projectId);
    if (vorher.status === 'vorhanden') return { status: 'vorhanden', art: ART, beschreibung: vorher.beschreibung };
    if (vorher.status === 'fehler') return fehlerErgebnis(vorher.fehler);

    let hb: Handbuch;
    try {
        hb = await ensureHandbuch({ nurAnsicht: false, status: 'entwurf' }, q.projectId);
    } catch (e: unknown) {
        return fehlerErgebnis(`Das Handbuch konnte nicht angelegt werden: ${describeError(e)}`);
    }
    // Wettlauf: zwischen Pruefung und Anlage hat jemand anderes angelegt.
    if (!istFrisch(hb)) return { status: 'vorhanden', art: ART, beschreibung: handbuchBestandText(hb) };

    const sb = steckbriefAusQuelle(q);
    const nummern = [...(sb.anhang2.nummern.wert || [])];
    const uebernommen: string[] = ['Handbuch als Entwurf angelegt — Baustellenordnung und Einrichtung bleiben leer, bis sie jemand beschließt.'];
    const offen: string[] = [];
    let seiten: HandbuchGewerk[] = [];

    let gewerke: GewerkAusPlan[] = [];
    let planGelesen = false;
    let ohnePlan = false;
    try {
        const stand = await ladePlanStand(q.projectId);
        planGelesen = true;
        ohnePlan = !stand.sigeProjektId;
        gewerke = [...stand.ausPlan.values()];
    } catch (e: unknown) {
        offen.push(`Gewerke aus dem SiGe-Plan nicht gelesen (${describeError(e)}) — im Handbuch „Gewerke aus dem Plan" nachziehen.`);
    }

    if (planGelesen && ohnePlan) {
        offen.push('Noch kein SiGe-Plan — Gewerkeseiten im Handbuch nachziehen, sobald der Plan Gewerke nennt.');
    } else if (planGelesen && !gewerke.length) {
        offen.push('Der SiGe-Plan nennt noch keine Gewerke — Gewerkeseiten im Handbuch nachziehen, sobald die Arbeitsschritte Gewerke tragen.');
    } else if (gewerke.length) {
        // `istFrisch` sieht nur den Kopfsatz: ein zweites Fenster kann das
        // Handbuch zeitgleich angelegt und schon Seiten nachgezogen haben.
        // Deshalb unmittelbar vor dem Schreiben die Seiten DIESES Handbuchs
        // lesen und nur anlegen, wenn es noch keine gibt.
        let vorhandene: HandbuchGewerk[] | null = null;
        try {
            vorhandene = await fetchHandbuchGewerke(hb.id);
        } catch (e: unknown) {
            offen.push(`Gewerkeseiten nicht angelegt — die vorhandenen Seiten ließen sich nicht lesen (${describeError(e)}); `
                + 'im Handbuch „Gewerke aus dem Plan" nachziehen.');
        }
        if (vorhandene && vorhandene.length) {
            // Nur fuer den Anhang-II-Satz: er rechnet gegen die echten Seiten.
            seiten = vorhandene;
            offen.push(gewerkeSchonDaSatz(vorhandene.length));
        } else if (vorhandene) {
            try {
                seiten = await createHandbuchGewerke(kontext(hb), hb.id, q.projectId, gewerkEntwuerfe(gewerke));
            } catch (e: unknown) {
                offen.push(`Gewerkeseiten nicht angelegt (${describeError(e)}) — im Handbuch „Gewerke aus dem Plan" nachziehen.`);
            }
            const ohneKatalog = gewerke
                .filter(g => !g.katalogId && seiten.some(s => s.gewerk_norm === g.gewerk_norm))
                .map(g => g.gewerk_label);
            const s = gewerkSaetze(seiten, ohneKatalog);
            uebernommen.push(...s.uebernommen);
            offen.push(...s.offen);

            // Nachweise je Seite einzeln — sie brauchen den Fremdschluessel der
            // eben entstandenen Seite (Muster `zieheNach` im Modul).
            const ohneNachweise: string[] = [];
            let nachweise = 0;
            for (const seite of seiten) {
                const quelle = gewerke.find(g => g.gewerk_norm === seite.gewerk_norm);
                const entwuerfe = nachweisEntwuerfe(quelle?.katalogId ?? null);
                if (!entwuerfe.length) continue;
                try {
                    nachweise += (await createHandbuchNachweise(kontext(hb), q.projectId, seite.id, entwuerfe)).length;
                } catch (e: unknown) {
                    ohneNachweise.push(`${seite.gewerk_label} (${describeError(e)})`);
                }
            }
            if (nachweise) uebernommen.push(`${nachweise} geforderte(r) Nachweis(e) aus den Gewerkvorlagen angelegt, alle mit Status „fehlt".`);
            if (ohneNachweise.length) {
                offen.push(`Nachweise nicht angelegt für ${aufzaehlung(ohneNachweise, 4)} — auf der Gewerkeseite ergänzen.`);
            }
        }
    }

    offen.push(...anhangIISaetze(nummern, sb.anhang2.offen, seiten));
    offen.push(REST_IM_HANDBUCH);
    return { status: 'angelegt', art: ART, uebernommen, offen };
};

const laufend = new Map<string, Promise<ErzeugungsErgebnis>>();

const legeAn = (q: PlanungsQuelle): Promise<ErzeugungsErgebnis> => {
    const schluessel = q?.projectId || '';
    const da = laufend.get(schluessel);
    if (da) return da;
    const lauf = legeAnOhneSperre(q)
        .catch((e: unknown) => fehlerErgebnis(`Das Handbuch konnte nicht angelegt werden: ${describeError(e)}`))
        .finally(() => { laufend.delete(schluessel); });
    laufend.set(schluessel, lauf);
    return lauf;
};

export const handbuchErzeuger: FolgeErzeuger = {
    art: ART,
    titel: TITEL,
    bestand: leseBestand,
    legeAn,
};
